/* eslint-disable radar/cognitive-complexity */
import {
  useState,
  DispatchWithoutAction,
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';

import * as Survey from 'survey-react';
import { HTMLMotionProps } from 'framer-motion';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BiCertification, BiInfoCircle, BiShield } from 'react-icons/bi';
import { useTheme } from 'styled-components';

import { SurveyQuestionType } from '@common/types/pusher/events';
import { SatisfactionSurveyAnswersTypes } from '@common/types/api/satisfaction-survey';
import { api } from '@services';
import {
  useAuth,
  useEventfy,
  useIsCertificateEnabled,
  useSatisfactionSurveyQuery,
} from '@hooks';
import {
  selectUser,
  selectUserHasRepliedSurvey,
  selectUpdateProfile,
} from '@hooks/useAuth/selectors';
import { DEFAULT_MODAL_ANIMATION, DEFAULT_TRANSITION } from '@constants';
import { Button } from '@components';

import * as S from './styles';

type SurveyModalBaseProps = HTMLMotionProps<'dialog'> & {
  handleClose: DispatchWithoutAction;
  handleCertificate: DispatchWithoutAction;
};

type SurveyModalProps = PropsWithChildren<SurveyModalBaseProps>;

const SurveyModal = (
  props: SurveyModalProps,
  ref: ForwardedRef<HTMLDialogElement>,
) => {
  const theme = useTheme();
  const event = useEventfy();
  const { name } = useAuth(selectUser);
  const isCertificateEnabled = useIsCertificateEnabled();

  const updateProfile = useAuth(selectUpdateProfile);
  const userHasRepliedSurvey = useAuth(selectUserHasRepliedSurvey);

  const satisfactionSurveyQuery = useSatisfactionSurveyQuery();

  const [userHasJustAnsweredSurvey, setUserHasJustAnsweredSurvey] =
    useState(false);

  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { children, handleClose, ...attrs } = props;

  const requiresPassword = event.certificate_password_required === 1;

  const surveyModel = useMemo(
    () =>
      new Survey.Model({
        locale: 'pt-br',
        showTitle: false,
        questionsOnPageMode: 'questionPerPage',
        showProgressBar: 'top',
        progressBarType: 'questions',
        goNextPageAutomatic: true,
        pages: [
          {
            elements: satisfactionSurveyQuery.data?.map((question, index) => {
              const title = question.asking;

              const baseData = {
                name: `${question.id}-question`,
                title,
                valueName: `${index}`,
                description: question.description,
                isRequired: question.required_answer,
                requiredErrorText: 'Resposta obrigatória',
              };

              if (question.asking_type === SurveyQuestionType.SINGLE_CHOICE) {
                return {
                  ...baseData,
                  type: 'radiogroup',
                  choices: question.answers.data.map((answer) => ({
                    value: answer.id,
                    text: {
                      'pt-br': answer.answer,
                    },
                  })),
                };
              }

              if (question.asking_type === SurveyQuestionType.MULTIPLE_CHOICE) {
                return {
                  ...baseData,
                  type: 'checkbox',
                  choices: question.answers.data.map(({ answer }) => answer),
                };
              }

              if (question.asking_type === SurveyQuestionType.RATING) {
                return {
                  ...baseData,
                  type: 'rating',
                  rateMin: question.rateMin,
                  rateMax: question.rateMax,
                  minRateDescription: question.minRateDescription,
                  maxRateDescription: question.maxRateDescription,
                };
              }

              return {
                ...baseData,
                type: 'text',
                placeholder: 'Escreva suas sugestões',
              };
            }),
          },
        ],
      }),
    [satisfactionSurveyQuery.data],
  );

  const handleSurveyCompletion = useCallback(
    async ({ valuesHash: results }: Survey.ReactSurveyModel) => {
      if (!satisfactionSurveyQuery?.data) return;

      const surveyPayload = Object.entries(satisfactionSurveyQuery.data).reduce(
        (acc, [index, question]) => {
          const key = +index;

          const answer = results[key];
          const isSingleChoice =
            question.asking_type === SurveyQuestionType.SINGLE_CHOICE;

          const isMultipleChoice =
            question.asking_type === SurveyQuestionType.MULTIPLE_CHOICE;

          if (isSingleChoice) {
            acc.pesquisa.push({
              survey_asking_id: question.id,
              asking_type: SurveyQuestionType.SINGLE_CHOICE,
              answer: answer || '',
            });
          } else if (isMultipleChoice) {
            acc.pesquisa.push({
              survey_asking_id: question.id,
              asking_type: SurveyQuestionType.MULTIPLE_CHOICE,
              answer: (answer as string[]) || [],
            });
          } else {
            acc.pesquisa.push({
              survey_asking_id: question.id,
              asking_type: SurveyQuestionType.TEXT,
              answer: answer || '',
            });
          }

          return acc;
        },
        { pesquisa: [] as SatisfactionSurveyAnswersTypes[] },
      );

      try {
        const { data: response } = await api.post<{ message: string }>(
          '/survey',
          surveyPayload,
        );

        toast.success(response.message);
        updateProfile({ survey_replied: 1 });
        setUserHasJustAnsweredSurvey(true);
      } catch (err) {
        const fallbackMessage =
          'Ocorreu um erro ao tentar enviar a sua resposta, tente novamente';

        const message =
          (err as AxiosError<{ message: string }>).response?.data.message ??
          fallbackMessage;
        toast.error(message);
      }
    },
    [satisfactionSurveyQuery.data, updateProfile],
  );

  const handleDownloadCertificate = useCallback(async () => {
    if (requiresPassword && !password) {
      toast.error('Por favor, digite a senha do certificado');
      return;
    }

    try {
      setIsLoading(true);

      const response = await api.post<Blob>(
        '/user/certificate',
        { password },
        {
          responseType: 'blob',
        },
      );

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Certificado ${name} - ${event.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      toast.success('Certificado baixado com sucesso!');
      handleClose();
    } catch (err) {
      const fallbackMessage = 'Erro ao baixar certificado';
      const message =
        (err as AxiosError<{ message: string }>).response?.data.message ??
        fallbackMessage;
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [requiresPassword, password, name, event.name, handleClose]);

  return (
    <S.Container
      open
      ref={ref}
      transition={DEFAULT_TRANSITION}
      variants={DEFAULT_MODAL_ANIMATION}
      {...attrs}
    >
      {children}

      <h2>Pesquisa de Satisfação</h2>

      {!userHasRepliedSurvey && satisfactionSurveyQuery.isSuccess && (
        <S.SurveyWrapper>
          <Survey.Survey
            model={surveyModel}
            onComplete={handleSurveyCompletion}
          />
        </S.SurveyWrapper>
      )}

      {userHasRepliedSurvey && (
        <S.FinishedSurveyWrapper>
          <div>
            <S.SuccessIconWrapper>
              <BiCertification size="8rem" color={theme.colors.success[100]} />
            </S.SuccessIconWrapper>

            <div>
              <S.TitleText>
                {userHasJustAnsweredSurvey
                  ? 'Obrigado por responder!'
                  : 'Você já respondeu!'}
              </S.TitleText>
              <S.SubtitleText>
                {userHasJustAnsweredSurvey
                  ? 'Sua opinião é muito importante para nós.'
                  : 'Sua resposta já foi registrada.'}
              </S.SubtitleText>
            </div>

            {isCertificateEnabled && (
              <>
                <S.SubtitleText>
                  {requiresPassword
                    ? 'Parabéns por concluir o evento! Digite sua senha para validar e baixar seu certificado de participação.'
                    : 'Parabéns por concluir o evento! Seu certificado de participação está pronto.'}
                </S.SubtitleText>

                {requiresPassword && (
                  <S.PasswordSection>
                    <S.PasswordLabel htmlFor="survey-certificate-password">
                      Senha de Validação
                    </S.PasswordLabel>
                    <S.PasswordInput
                      id="survey-certificate-password"
                      type="text"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Digite sua senha"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && password) {
                          handleDownloadCertificate();
                        }
                      }}
                    />
                    <S.PasswordHint>
                      <BiInfoCircle size={14} />A senha foi informada durante a
                      transmissão.
                    </S.PasswordHint>

                    <S.InfoBox>
                      <BiShield size={20} color={theme.colors.warning[100]} />
                      <p>
                        Seu certificado será emitido com base no nome informado
                        em seu cadastro na plataforma.
                      </p>
                    </S.InfoBox>
                  </S.PasswordSection>
                )}

                <S.ButtonWrapper>
                  <Button
                    title={
                      requiresPassword
                        ? 'Validar e Baixar Certificado'
                        : 'Baixar Certificado'
                    }
                    loading={isLoading}
                    onClick={handleDownloadCertificate}
                    disabled={isLoading}
                  />
                </S.ButtonWrapper>
              </>
            )}
          </div>
        </S.FinishedSurveyWrapper>
      )}
    </S.Container>
  );
};

export default forwardRef(SurveyModal);
